import React, { useState } from "react";
import {
  getAllMerchants,
  getAllSettlements,
  createSettlement,
  updateSettlementStatus,
} from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  MoreHorizontal,
  FileText,
  Download,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Eye,
  Send,
} from "lucide-react";
import { format } from "date-fns";

const statusConfig = {
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700" },
  completed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Declined" },
};

export default function Settlements() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const { t } = useLanguage();

  const queryClient = useQueryClient();

  const { data: settlements = [], isLoading } = useQuery({
    queryKey: ["settlements"],
    queryFn: () => getAllSettlements(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => getAllMerchants(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => createSettlement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settlements"] });
      setCreateOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateSettlementStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settlements"] });
    },
  });

  const filteredSettlements = settlements.filter((s) => {
    if (search) {
      const searchLower = search.toLowerCase();
      if (
        !s.settlement_id?.toLowerCase().includes(searchLower) &&
        !s.merchant_code?.toLowerCase().includes(searchLower)
      ) {
        return false;
      }
    }
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    return true;
  });

  const handleProcess = async (settlement) => {
    await updateMutation.mutateAsync({
      id: settlement.id,
      data: { status: "processing" },
    });
  };

  const handleComplete = async (settlement) => {
    await updateMutation.mutateAsync({
      id: settlement.id,
      data: { status: "completed", processed_at: new Date().toISOString() },
    });
  };

  const handleExport = (settlement) => {
    try {
      const headers = [
        "Settlement ID",
        "Merchant",
        "Period Start",
        "Period End",
        "Deposits",
        "Withdrawals",
        "Fees",
        "Net Amount",
        "Status",
        "Currency"
      ];
      
      const row = [
        settlement.settlement_id || "",
        settlement.merchant_code || "",
        settlement.period_start ? format(new Date(settlement.period_start), "yyyy-MM-dd") : "",
        settlement.period_end ? format(new Date(settlement.period_end), "yyyy-MM-dd") : "",
        settlement.total_deposits || 0,
        settlement.total_withdrawals || 0,
        settlement.total_fees || 0,
        settlement.net_amount || 0,
        settlement.status || "",
        settlement.currency || "PKR"
      ].map(field => `"${field}"`);
      
      const csvContent = headers.join(",") + "\n" + row.join(",");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `settlement_${settlement.settlement_id || 'report'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("settlementTitle")}</h1>
          <p className="text-slate-500">{t("merchantPayoutManagement")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 hover:scale-105 transition-all duration-300 shadow-sm hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-700">
          <Plus className="h-4 w-4" />
          {t("createSettlement")}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <div className="relative min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("searchSettlements")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[170px]">
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allStatus")}</SelectItem>
              <SelectItem value="pending" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("pending")}</SelectItem>
              <SelectItem value="processing" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("processing")}</SelectItem>
              <SelectItem value="completed" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("completed")}</SelectItem>
              <SelectItem value="declined" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("declined") || "Declined"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {filteredSettlements.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 text-center text-slate-500">
            {isLoading ? t("loading") : t("noSettlements")}
          </div>
        ) : (
          filteredSettlements.map((settlement, index) => {
            const status = statusConfig[settlement.status] || statusConfig.pending;
            const StatusIcon = status.icon;

            return (
              <motion.div
                key={settlement.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.02 * index }}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="font-mono text-sm font-medium truncate">{settlement.settlement_id}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-2 font-medium">{settlement.merchant_code}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {settlement.period_start ? format(new Date(settlement.period_start), "MMM d") : t("nA")} to {settlement.period_end ? format(new Date(settlement.period_end), "MMM d, yyyy") : t("nA")}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setSelectedSettlement(settlement)}>
                        <Eye className="h-4 w-4 mr-2" />
                        {t("viewDetails")}
                      </DropdownMenuItem>
                      {settlement.status === "pending" && (
                        <DropdownMenuItem onClick={() => handleProcess(settlement)}>
                          <Send className="h-4 w-4 mr-2" />
                          {t("process")}
                        </DropdownMenuItem>
                      )}
                      {settlement.status === "processing" && (
                        <DropdownMenuItem onClick={() => handleComplete(settlement)}>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          {t("markCompleted")}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleExport(settlement)}>
                        <Download className="h-4 w-4 mr-2" />
                        {t("exportReport")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">{t("deposits")}</p>
                    <p className="font-medium text-blue-600">+RS{settlement.total_deposits?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t("withdrawals")}</p>
                    <p className="font-medium text-emerald-600">-RS{settlement.total_withdrawals?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t("netAmount")}</p>
                    <p className={`font-bold ${(settlement.net_amount || 0) < 0 ? "text-red-500" : "text-slate-900"}`}>
                      {(settlement.net_amount || 0) < 0 ? `-RS${Math.abs(settlement.net_amount).toLocaleString()}` : `RS${(settlement.net_amount || 0).toLocaleString()}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t("status")}</p>
                    <Badge className={`${status.color} gap-1 border-0 mt-1`}>
                      <StatusIcon className={`h-3 w-3 ${settlement.status === "processing" ? "animate-spin" : ""}`} />
                      {t(settlement.status || "pending")}
                    </Badge>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Table */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden overflow-x-auto">
        <Table className="min-w-[800px]">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">{t("settlementId")}</TableHead>
              <TableHead className="font-semibold">{t("merchant")}</TableHead>
              <TableHead className="font-semibold">{t("period")}</TableHead>
              <TableHead className="font-semibold text-right">{t("deposits")}</TableHead>
              <TableHead className="font-semibold text-right">{t("withdrawals")}</TableHead>
              <TableHead className="font-semibold text-right">{t("netAmount")}</TableHead>
              <TableHead className="font-semibold">{t("status")}</TableHead>
              <TableHead className="font-semibold w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSettlements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  {isLoading ? t("loading") : t("noSettlements")}
                </TableCell>
              </TableRow>
            ) : (
              filteredSettlements.map((settlement, index) => {
                const status = statusConfig[settlement.status] || statusConfig.pending;
                const StatusIcon = status.icon;

                return (
                  <motion.tr
                    key={settlement.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.02 * index }}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="font-mono text-sm">{settlement.settlement_id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{settlement.merchant_code}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{settlement.period_start ? format(new Date(settlement.period_start), "MMM d") : "N/A"}</div>
                        <div className="text-slate-500">
                          to {settlement.period_end ? format(new Date(settlement.period_end), "MMM d, yyyy") : "N/A"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium text-blue-600">
                      +RS{settlement.total_deposits?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">
                      -RS{settlement.total_withdrawals?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-bold ${(settlement.net_amount || 0) < 0 ? "text-red-500" : "text-slate-900"}`}>
                        {(settlement.net_amount || 0) < 0 ? `-RS${Math.abs(settlement.net_amount).toLocaleString()}` : `RS${(settlement.net_amount || 0).toLocaleString()}`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${status.color} gap-1 border-0`}>
                        <StatusIcon className={`h-3 w-3 ${settlement.status === "processing" ? "animate-spin" : ""}`} />
                        {t(settlement.status || "pending")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setSelectedSettlement(settlement)}>
                            <Eye className="h-4 w-4 mr-2" />
                            {t("viewDetails")}
                          </DropdownMenuItem>
                          {settlement.status === "pending" && (
                            <DropdownMenuItem onClick={() => handleProcess(settlement)}>
                              <Send className="h-4 w-4 mr-2" />
                              {t("process")}
                            </DropdownMenuItem>
                          )}
                          {settlement.status === "processing" && (
                            <DropdownMenuItem onClick={() => handleComplete(settlement)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              {t("markCompleted")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleExport(settlement)}>
                            <Download className="h-4 w-4 mr-2" />
                            {t("exportReport")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </motion.tr>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Settlement Dialog */}
      <CreateSettlementDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        merchants={merchants}
        onCreate={(data) => createMutation.mutateAsync(data)}
      />

      {/* Settlement Details Dialog */}
      {selectedSettlement && (
        <Dialog open={true} onOpenChange={(open) => !open && setSelectedSettlement(null)}>
          <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("viewDetails")}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-slate-500 block">{t("settlementId")}</span>
                  <span className="font-mono text-sm font-medium">{selectedSettlement.settlement_id}</span>
                </div>
                <div>
                  <span className="text-sm text-slate-500 block">{t("merchant")}</span>
                  <span className="font-medium text-sm">{selectedSettlement.merchant_code}</span>
                </div>
                <div>
                  <span className="text-sm text-slate-500 block">{t("period")}</span>
                  <div className="text-sm font-medium">
                    {selectedSettlement.period_start ? format(new Date(selectedSettlement.period_start), "MMM d") : "N/A"}
                    {" - "}
                    {selectedSettlement.period_end ? format(new Date(selectedSettlement.period_end), "MMM d, yyyy") : "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-slate-500 block">{t("status")}</span>
                  {(() => {
                    const status = statusConfig[selectedSettlement.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    return (
                      <Badge className={`${status.color} gap-1 border-0 mt-1 inline-flex`}>
                        <StatusIcon className={`h-3 w-3 ${selectedSettlement.status === "processing" ? "animate-spin" : ""}`} />
                        {t(selectedSettlement.status || "pending")}
                      </Badge>
                    );
                  })()}
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">{t("settlementTotalDeposits")}</span>
                  <span className="font-medium text-blue-600">+RS{selectedSettlement.total_deposits?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">{t("settlementTotalWithdrawals")}</span>
                  <span className="font-medium text-emerald-600">-RS{selectedSettlement.total_withdrawals?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">{t("totalFees")}</span>
                  <span className="font-medium text-red-500">-RS{selectedSettlement.total_fees?.toLocaleString() || 0}</span>
                </div>
                <div className="pt-3 border-t border-slate-200 flex justify-between items-center">
                  <span className="font-semibold text-slate-900">{t("netAmount")}</span>
                  <span className={`text-lg font-bold ${(selectedSettlement.net_amount || 0) < 0 ? "text-red-500" : "text-slate-900"}`}>
                    {(selectedSettlement.net_amount || 0) < 0 ? `-RS${Math.abs(selectedSettlement.net_amount).toLocaleString()}` : `RS${(selectedSettlement.net_amount || 0).toLocaleString()}`}
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button onClick={() => setSelectedSettlement(null)}>{t("cancel")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function CreateSettlementDialog({ open, onClose, merchants, onCreate }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    merchant_id: "",
    period_start: "",
    period_end: "",
    total_deposits: 0,
    total_withdrawals: 0,
    total_fees: 0,
    net_amount: 0,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  React.useEffect(() => {
    const net = (formData.total_deposits || 0) - (formData.total_withdrawals || 0) - (formData.total_fees || 0);
    setFormData((prev) => ({ ...prev, net_amount: net }));
  }, [formData.total_deposits, formData.total_withdrawals, formData.total_fees]);

  React.useEffect(() => {
    if (!open) {
      setFormData({
        merchant_id: "",
        period_start: "",
        period_end: "",
        total_deposits: 0,
        total_withdrawals: 0,
        total_fees: 0,
        net_amount: 0,
      });
      setSaving(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSaving(true);
    const settlementId = "STL" + Date.now().toString().slice(-8);
    const merchant = merchants.find((m) => m.id === formData.merchant_id);

    try {
      await onCreate({
        settlement_id: settlementId,
        merchant_id: formData.merchant_id,
        merchant_code: merchant?.code,
        period_start: formData.period_start,
        period_end: formData.period_end,
        status: "pending",
        total_deposits: parseFloat(formData.total_deposits) || 0,
        total_withdrawals: parseFloat(formData.total_withdrawals) || 0,
        total_fees: parseFloat(formData.total_fees) || 0,
        net_amount: formData.net_amount,
        currency: "PKR",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("createSettlement")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t("merchant")} *</Label>
            <Select
              value={formData.merchant_id}
              onValueChange={(v) => handleChange("merchant_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("selectMerchant")} />
              </SelectTrigger>
              <SelectContent>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("periodStart")} *</Label>
              <Input
                type="date"
                value={formData.period_start}
                onChange={(e) => handleChange("period_start", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("periodEnd")} *</Label>
              <Input
                type="date"
                value={formData.period_end}
                onChange={(e) => handleChange("period_end", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("settlementTotalDeposits")} (PKR)</Label>
            <Input
              type="number"
              value={formData.total_deposits}
              onChange={(e) => handleChange("total_deposits", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("settlementTotalWithdrawals")} (PKR)</Label>
            <Input
              type="number"
              value={formData.total_withdrawals}
              onChange={(e) => handleChange("total_withdrawals", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("totalFees")} (PKR)</Label>
            <Input
              type="number"
              value={formData.total_fees}
              onChange={(e) => handleChange("total_fees", e.target.value)}
            />
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">{t("netAmount")}:</span>
              <span className="text-xl font-bold text-slate-900">
                RS{formData.net_amount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.merchant_id || !formData.period_start || !formData.period_end}
          >
            {saving ? t("creating") : t("createPayout")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}